import { useAPI } from "@/hooks/useAPI";
import { MedChronServiceV2 } from "@/services/medChronServiceV2";
import { SummaryServiceV2 } from "@/services/summaryServiceV2";
import type { MedPatientBackground, MedPatientBackgroundUpdate } from "@/types/medchron";
import type { MedSummaryData, MedSummaryDataUpdate } from "@/types/summary";
import { useMemo } from "react";
import useSWR from "swr";

const CASE_SUMMARY_DATA_KEY = "/medchron/summary";
const CASE_PATIENT_BACKGROUND_KEY = "/medchron/patient-background";

export interface UseCaseSummaryParams {
  caseId: string | null | undefined;
  versionId: string | null | undefined;
}

export const useCaseSummary = ({ caseId, versionId }: UseCaseSummaryParams) => {
  const apiV2 = useAPI("v2");
  const summaryService = useMemo(() => new SummaryServiceV2(apiV2), [apiV2]);
  const medChronServiceV2 = useMemo(() => new MedChronServiceV2(apiV2), [apiV2]);

  const summaryKey =
    caseId && versionId ? ([CASE_SUMMARY_DATA_KEY, caseId, versionId] as const) : null;
  const backgroundKey =
    caseId && versionId ? ([CASE_PATIENT_BACKGROUND_KEY, caseId, versionId] as const) : null;

  const {
    data: summaryData,
    error: summaryError,
    isLoading: isSummaryLoading,
    mutate: mutateSummary,
  } = useSWR<MedSummaryData>(
    summaryKey,
    () => {
      if (!caseId || !versionId) {
        throw new Error("caseId and versionId are required");
      }
      return summaryService.getSummary(caseId, versionId);
    },
    { revalidateOnFocus: false }
  );

  const {
    data: patientBackground,
    error: backgroundError,
    isLoading: isBackgroundLoading,
    mutate: mutateBackground,
  } = useSWR<MedPatientBackground>(
    backgroundKey,
    () => {
      if (!caseId || !versionId) {
        throw new Error("caseId and versionId are required");
      }
      return medChronServiceV2.getPatientBackground(caseId, versionId);
    },
    { revalidateOnFocus: false }
  );

  const patchSummary = async (data: MedSummaryDataUpdate) => {
    if (!caseId || !versionId) {
      throw new Error("caseId and versionId are required");
    }
    const updated = await summaryService.updateSummary(caseId, versionId, data);
    await mutateSummary(updated, { revalidate: false });
    return updated;
  };

  const patchPatientBackground = async (data: MedPatientBackgroundUpdate) => {
    if (!caseId || !versionId) {
      throw new Error("caseId and versionId are required");
    }
    const updated = await medChronServiceV2.patchPatientBackground(caseId, versionId, data);
    await mutateBackground(updated, { revalidate: false });
    return updated;
  };

  return {
    summaryData: summaryData ?? null,
    patientBackground: patientBackground ?? null,
    isLoading: isSummaryLoading || isBackgroundLoading,
    summaryError,
    backgroundError,
    patchSummary,
    patchPatientBackground,
    revalidate: async () => {
      await Promise.all([mutateSummary(), mutateBackground()]);
    },
  };
};
